<script setup>
import { ref, computed, watch } from 'vue';

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

import { useProjectStore } from '@/stores/project';

import { useBugReport } from '@/composables/useBugReport';

import AppSidebar from '@/components/general/Sidebar.vue';
import AppThemeToggleButton from '@/components/general/ThemeToggleButton.vue';
import AppProfilePopover from '@/components/profile/ProfilePopover.vue';
import AppNavbarLogo from '@/components/general/NavbarLogo.vue';

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = computed(() => breakpoints.smaller('sm'));

const projectStore = useProjectStore();
const { openSentryBugReportWidget } = useBugReport();

const documentationUrl = import.meta.env.VITE_DOCUMENTATION_LINK;

const sidebarVisible = ref(false);

const currentProject = computed(() => projectStore.currentProject);

// Watch for mobile and project changes
watch(
  [isMobile, currentProject],
  () => {
    if (!currentProject.value && isMobile.value.value) {
      sidebarVisible.value = true;
    }
  },
  { immediate: true }
);
</script>

<template>
  <Toolbar
    class="!content-border-color rounded-none border-0 border-b border-solid !bg-surface-0 shadow-none dark:!bg-surface-800"
  >
    <template #start>
      <button
        icon
        @click="sidebarVisible = true"
        class="flex items-center md:hidden"
      >
        <i class="icon-[prime--bars] text-4xl" v-tooltip.bottom="'Menu'" />
      </button>
    </template>
    <template #end>
      <!-- bugs -->
      <Button
        text
        v-tooltip.bottom="'Report a bug'"
        severity="secondary"
        class="-my-2 mr-3 !p-1.5"
        size="small"
        @click="openSentryBugReportWidget"
      >
        <i class="icon-[ph--bug] text-2xl" />
      </Button>
      <!-- ideas -->
      <Button
        as="a"
        href="https://feedback.cognitivecloud.com "
        target="_blank"
        rel="noopener"
        text
        v-tooltip.bottom="'Feedback'"
        severity="secondary"
        class="-my-2 mr-3 !p-1.5"
        size="small"
      >
        <i class="icon-[teenyicons--bulb-on-outline] -mt-0.5 text-2xl" />
      </Button>
      <!-- support  -->
      <Button
        as="a"
        href="https://support.cognitivecloud.com"
        target="_blank"
        rel="noopener"
        text
        v-tooltip.bottom="'Support'"
        severity="secondary"
        class="-my-2 mr-3 !p-1.5"
        size="small"
      >
        <i class="icon-[ph--question] text-2xl" />
      </Button>
      <!-- documentation -->
      <Button
        as="a"
        :href="documentationUrl"
        target="_blank"
        rel="noopener"
        text
        v-tooltip.bottom="'API documentation'"
        severity="secondary"
        class="-my-2 mr-3 !p-1.5"
        size="small"
      >
        <i class="icon-[ph--code-simple] text-2xl" />
      </Button>
      <app-theme-toggle-button />
      <app-profile-popover />
    </template>
  </Toolbar>
  <Drawer
    v-model:visible="sidebarVisible"
    class="sidebar-width"
    pt:content:class="!p-0"
    :ptOptions="{ mergeProps: true }"
    :dismissable="projectStore.currentProject ? true : false"
    :showCloseIcon="projectStore.currentProject ? true : false"
  >
    <template #header>
      <!-- Extra span to keep the close button on the right side -->
      <span class="hidden md:inline"></span>
      <app-navbar-logo class="-my-4 -ml-1 md:hidden" />
    </template>
    <app-sidebar
      :key="projectStore.currentProjectId"
      @close-sidebar="sidebarVisible = false"
    />
  </Drawer>
</template>
